/**
 * The program strip's state: Housekeeping · Companion · Reading & Services — the program switch for the whole app.
 * People (1.8.11) keeps its choices inside the page, including Everyone and No program yet; they are People's alone, so the
 * other pages keep the program last chosen.
 *
 * 1.5.16: the last program follows the person (src/lib/my-settings.ts): a tab that has not chosen one yet starts
 * on it, and a choice here is saved for their next device. A tab that has chosen keeps its own choice.
 */
import { useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isProgramView, type PeopleView } from '@shared/people-programs';
import { MY_PROGRAMS } from '@shared/my-settings';
import { usePrefs } from '@/lib/prefs';
import { PROGRAM_PATH } from './nav';

export const PROGRAMS = MY_PROGRAMS;
export type Program = (typeof PROGRAMS)[number];
/** The two choices People adds to the row. */
export const PEOPLE_ONLY = ['everyone', 'none'] as const;
type PeopleOnly = (typeof PEOPLE_ONLY)[number];

import { ProgramCtxValue as Ctx } from './program-context';
const KEY = 'abf.program';
const PEOPLE_KEY = 'abf.people.view';

function remembered<T extends string>(key: string, allowed: readonly T[]): T | null {
  try {
    const v = sessionStorage.getItem(key);
    return (allowed as readonly string[]).includes(v ?? '') ? (v as T) : null;
  } catch {
    return null;
  }
}

function remember(key: string, value: string | null) {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * `housekeepingOnly`: a role with no program strip (the scheduler, the crew, the tech) is always on Housekeeping.
 * `today` (1.8.4, D-1.8.4-1): the role has the house Today — `/` is always Today for it (26 Oct 2026); the program kept here is
 * only the last space chosen (Book, People and the saved preference read it), never what `/` shows.
 */
export function ProgramProvider({ children, housekeepingOnly = false, today = false }: { children: ReactNode; housekeepingOnly?: boolean; today?: boolean }) {
  const { prefs, setPrefs } = usePrefs();
  const hasToday = today && !housekeepingOnly;
  const [chosen, set] = useState<Program | null>(() => remembered(KEY, PROGRAMS));
  const program: Program = housekeepingOnly ? 'housekeeping' : (chosen ?? prefs.program ?? 'housekeeping');
  const [only, setOnly] = useState<PeopleOnly | null>(() => remembered(PEOPLE_KEY, PEOPLE_ONLY));
  const value = useMemo(() => {
    const setProgram = (p: Program) => {
      set(p);
      setOnly(null);
      remember(KEY, p);
      remember(PEOPLE_KEY, null);
      if (!housekeepingOnly) setPrefs({ program: p });
    };
    return {
      program,
      setProgram,
      hasToday,
      peopleView: only ?? program,
      setPeopleView: (v: PeopleView) => {
        if (isProgramView(v)) return setProgram(v);
        setOnly(v);
        remember(PEOPLE_KEY, v);
      },
    };
  }, [program, only, housekeepingOnly, setPrefs, hasToday]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProgram() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useProgram outside ProgramProvider');
  return v;
}

/**
 * 1.8.4 (D-1.8.4-3): "Open in [program]" from Today — the strip moves to that program's tab and the app goes to the
 * address (a Housekeeping line opens Housekeeping's own day, `/housekeeping` or `/housekeeping?day=`, not Today).
 */
export function useOpenIn(): (href: string, program?: Program | null) => void {
  const { setProgram } = useProgram();
  const navigate = useNavigate();
  return useCallback(
    (href: string, program?: Program | null) => {
      if (program) setProgram(program);
      // 25 Oct 2026 (D-1025-NAV): the old way of choosing a program (state, then `/`) goes straight to its address (26 Oct: Housekeeping too)
      const at = program && /^\/(\?|$)/.test(href) ? `${PROGRAM_PATH[program]}${href.slice(1)}` : href;
      navigate(at);
    },
    [setProgram, navigate],
  );
}
